/**
 * Per-tenant tekst-overrides (ADR-0021).
 *
 * Verantwoordelijkheden:
 *
 *  1. Pre-login: één publieke fetch op `/api/v1/i18n/overrides` levert
 *     de bundle voor alle beschikbare talen van deze tenant. We mergen
 *     defaults + overrides per locale en zetten ze via i18next's
 *     `addResourceBundle(locale, ns, msgs, deep, overwrite)` — `overwrite`
 *     zorgt dat een verwijderde override terugvalt op de default (we
 *     herinstalleren immers de gehele merged tree).
 *
 *  2. Admin-CRUD: tenant-admins kunnen overrides upserten/wissen/
 *     resetten-per-namespace. De Editor-view (M9) gebruikt deze store
 *     direct; iedere mutation re-mergt de betreffende locale.
 *
 * i18next gebruikt single-brace `{name}` interpolation (zie i18n/index.ts);
 * onze JSON-bundles en overrides volgen dezelfde syntax — geen
 * placeholder-conversie nodig.
 */
import { create } from 'zustand';
import { api } from '@shared/api/client';
import { i18n, i18nDefaults, type SpaLocale } from '@shared/i18n';

export interface LocaleOverrideRow {
  id: number;
  locale: string;
  key: string;
  value: string;
  updated_at: string | null;
}

export interface LocaleOverrideHistoryRow {
  id: number;
  action: string;
  actor_id: string | null;
  actor_type: string | null;
  impersonator_user_id: string | null;
  superadmin_origin_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  created_at: string;
}

type PublicBundle = Record<string, Record<string, string>>;

function expandDotNotation(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let cursor: Record<string, unknown> = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (typeof cursor[part] !== 'object' || cursor[part] === null) {
        cursor[part] = {};
      }
      cursor = cursor[part] as Record<string, unknown>;
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return out;
}

function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = JSON.parse(JSON.stringify(target));
  for (const [key, value] of Object.entries(source)) {
    const existing = out[key];
    if (
      value !== null
      && typeof value === 'object'
      && !Array.isArray(value)
      && existing !== null
      && typeof existing === 'object'
      && !Array.isArray(existing)
    ) {
      out[key] = deepMerge(existing as Record<string, unknown>, value as Record<string, unknown>);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function isKnownLocale(locale: string): locale is SpaLocale {
  return locale === 'en' || locale === 'nl' || locale === 'de' || locale === 'fr';
}

interface LocaleOverridesState {
  bundle: PublicBundle;
  keysWithHistory: Record<string, Set<string>>;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchPublic: () => Promise<void>;
  fetchAll: () => Promise<LocaleOverrideRow[]>;
  upsert: (payload: { locale: string; key: string; value: string; required_placeholders?: string[] }) => Promise<LocaleOverrideRow>;
  remove: (locale: string, key: string) => Promise<void>;
  resetNamespace: (locale: string, namespace: string) => Promise<number>;
  fetchHistory: (locale: string, key: string) => Promise<LocaleOverrideHistoryRow[]>;
  hasHistory: (locale: string, key: string) => boolean;
  reset: () => void;
  /**
   * Re-publish naar i18next: vervangt het volledige resource-bundle
   * voor één locale met (default + overrides). `overwrite=true`
   * zorgt voor de "reset"-semantiek bij DELETE — verwijderde keys
   * vallen weer op de default terug zonder reload.
   */
  applyToI18n: (locale: string) => void;
}

export const useLocaleOverridesStore = create<LocaleOverridesState>((set, get) => ({
  bundle: {},
  keysWithHistory: {},
  loading: false,
  saving: false,
  error: null,

  applyToI18n(locale) {
    if (!isKnownLocale(locale)) return;
    const overrides = get().bundle[locale] ?? {};
    const merged = deepMerge(i18nDefaults[locale], expandDotNotation(overrides));
    // i18next: addResourceBundle met deep=true, overwrite=true vervangt
    // exact deze locale + namespace. Onze i18n-init gebruikt namespace
    // 'translation' (de default).
    i18n.addResourceBundle(locale, 'translation', merged, true, true);
  },

  async fetchPublic() {
    set({ loading: true });
    try {
      const { data } = await api.get<{ data: PublicBundle }>('/api/v1/i18n/overrides');
      set({ bundle: data.data ?? {} });
      for (const locale of Object.keys(get().bundle)) {
        get().applyToI18n(locale);
      }
    } catch (e) {
      const anyE = e as { response?: { data?: { message?: string } } };
      set({ error: anyE.response?.data?.message ?? 'Failed to load overrides' });
    } finally {
      set({ loading: false });
    }
  },

  async fetchAll() {
    set({ loading: true });
    try {
      const { data } = await api.get<{
        data: LocaleOverrideRow[];
        keys_with_history?: Record<string, string[]>;
      }>('/api/v1/locale-overrides');
      const fresh: PublicBundle = {};
      for (const row of data.data) {
        (fresh[row.locale] ??= {})[row.key] = row.value;
      }
      const hist: Record<string, Set<string>> = {};
      for (const [locale, keys] of Object.entries(data.keys_with_history ?? {})) {
        hist[locale] = new Set(keys);
      }
      set({ bundle: fresh, keysWithHistory: hist });
      for (const locale of Object.keys(fresh)) get().applyToI18n(locale);
      return data.data;
    } finally {
      set({ loading: false });
    }
  },

  async upsert(payload) {
    set({ saving: true, error: null });
    try {
      const { data } = await api.put<{ data: LocaleOverrideRow }>('/api/v1/locale-overrides', payload);
      const nextBundle = { ...get().bundle };
      (nextBundle[payload.locale] ??= { ...(nextBundle[payload.locale] ?? {}) })[payload.key] = payload.value;

      const nextHistory = { ...get().keysWithHistory };
      const localeSet = new Set(nextHistory[payload.locale] ?? []);
      localeSet.add(payload.key);
      nextHistory[payload.locale] = localeSet;

      set({ bundle: nextBundle, keysWithHistory: nextHistory });
      get().applyToI18n(payload.locale);
      return data.data;
    } catch (e) {
      const anyE = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const first = anyE.response?.data?.errors
        ? Object.values(anyE.response.data.errors)[0]?.[0]
        : null;
      set({ error: first ?? anyE.response?.data?.message ?? 'Save failed' });
      throw e;
    } finally {
      set({ saving: false });
    }
  },

  async remove(locale, key) {
    set({ saving: true, error: null });
    try {
      await api.delete('/api/v1/locale-overrides', { data: { locale, key } });
      const nextBundle = { ...get().bundle };
      if (nextBundle[locale]) {
        const localeBundle = { ...nextBundle[locale] };
        delete localeBundle[key];
        nextBundle[locale] = localeBundle;
      }
      set({ bundle: nextBundle });
      get().applyToI18n(locale);
    } finally {
      set({ saving: false });
    }
  },

  async resetNamespace(locale, namespace) {
    set({ saving: true, error: null });
    try {
      const { data } = await api.post<{ status: string; count: number }>(
        '/api/v1/locale-overrides/reset-namespace',
        { locale, namespace },
      );
      const nextBundle = { ...get().bundle };
      if (nextBundle[locale]) {
        const localeBundle = { ...nextBundle[locale] };
        for (const k of Object.keys(localeBundle)) {
          if (k === namespace || k.startsWith(namespace + '.')) {
            delete localeBundle[k];
          }
        }
        nextBundle[locale] = localeBundle;
      }
      set({ bundle: nextBundle });
      get().applyToI18n(locale);
      return data.count;
    } finally {
      set({ saving: false });
    }
  },

  async fetchHistory(locale, key) {
    const { data } = await api.get<{ data: LocaleOverrideHistoryRow[] }>(
      '/api/v1/locale-overrides/history',
      { params: { locale, key } },
    );
    return data.data;
  },

  hasHistory(locale, key) {
    return get().keysWithHistory[locale]?.has(key) ?? false;
  },

  reset() {
    set({ bundle: {}, keysWithHistory: {}, error: null });
  },
}));
